﻿import { faTimes } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Col, Layout, Row, Tooltip } from 'antd';
import { observer } from 'mobx-react';
import React, { CSSProperties, ReactNode, useEffect, useState } from 'react';
import { Resizable, ResizableProps, ResizeCallbackData } from 'react-resizable';

export const DEFAULT_PANEL_HEIGHT = 320;
export const DEFAULT_BORDER = '1px solid rgb(227, 229, 232)';

export interface Props {    
    onClose?: () => void,
    header: ReactNode,
    headerMenu?: ReactNode,
    hideHeader?: boolean,
    disableResizing?: boolean,
    enableTransitions?: boolean
    content: ReactNode,
    panelClassName?: string,
    defaultHeight?: number,
    resizeProps?: Partial<ResizableProps>,    
    panelStyle?: CSSProperties,
    contentStyle?: CSSProperties,
    headerStyle?: CSSProperties
}

const MapLowerPanel: React.FC<Props> = ({      
    onClose,   
    header,
    headerMenu,
    hideHeader,
    disableResizing,
    enableTransitions,
    content,
    panelClassName = 'map-lower-panel',
    defaultHeight = DEFAULT_PANEL_HEIGHT,
    resizeProps = {},
    panelStyle,
    contentStyle,
    headerStyle
}) => {

    const {
        onResize,
        onResizeStart,
        onResizeStop,
        ...panelRestProps
    } = resizeProps;

    const [height, setHeight] = useState<number | undefined>(DEFAULT_PANEL_HEIGHT);
    const [resizing, setResizing] = React.useState(false);

    useEffect(() => {
        setHeight(content ? defaultHeight : undefined);
    }, [defaultHeight, content])

    const handleResize = (e: React.SyntheticEvent<Element, Event>, data: ResizeCallbackData) => {
        if (!disableResizing) {
            setHeight(data.size.height);
            if (onResize)
                onResize(e, data);
        }
    }   

    const hansleResizeStart = (e: React.SyntheticEvent<Element, Event>, data: ResizeCallbackData) => {
        if (onResizeStart)
            onResizeStart(e, data);

        setResizing(true);    
    }

    const hansleResizeStop = (e: React.SyntheticEvent<Element, Event>, data: ResizeCallbackData) => {
        if (onResizeStop)
            onResizeStop(e, data);

        setResizing(false);  
    }

    const renderPanel = () => {
        return (
            <div style={{
                height,
                position: 'relative',
                width: '100%',
                borderTop: DEFAULT_BORDER, 
                transition: (enableTransitions && (!resizing || disableResizing)) ? 'all 0.2s' : 'none',
                ...panelStyle
            }}>
                <Layout style={{     
                    height: 'inherit',  
                    width: 'inherit',    
                }}>
                    {
                        !hideHeader && (
                            <Row wrap={false} align='middle' justify='center'
                                style={{
                                    borderBottom: DEFAULT_BORDER,
                                    padding: '2px 8px',
                                    gap: 4,
                                    ...headerStyle
                                }}
                            >
                                <Col flex='none'>
                                    {header}
                                </Col>
                                <Col flex='auto'>
                                    {headerMenu}
                                </Col>
                                {onClose && <Col flex='none'>
                                    <Tooltip title='Close' placement='right' arrowPointAtCenter>
                                        <Button type='text' className={`${panelClassName}-close-btn`}
                                            icon={<FontAwesomeIcon icon={faTimes} fixedWidth />}
                                            onClick={() => {
                                                onClose();
                                            }}
                                        />
                                    </Tooltip>
                                </Col>}
                            </Row>
                        )
                    }                
                    <Layout style={{
                        height: 'inherit',
                        width: 'inherit',
                        ...contentStyle
                    }}>
                        {content }
                    </Layout> 
                </Layout>
            </div>
        )
    }

    if (height === undefined) 
        return renderPanel();

    return (
        <Resizable
            width={100}
            height={height}
            resizeHandles={['n']}
            axis="y"
            handle={<span style={{
                ...DEFAULT_HANDLE_STYLE,
                display: disableResizing ? 'none' : '',
            }}></span>}
            onResize={handleResize}
            onResizeStart={hansleResizeStart}
            onResizeStop={hansleResizeStop}
            {...panelRestProps}>
            {renderPanel()}            
        </Resizable>       
    )    
}

export default observer(MapLowerPanel);

const DEFAULT_HANDLE_STYLE: CSSProperties = {
    position: 'absolute',
    zIndex: 500,
    height: 6,
    left: 0,
    right: 0,
    top: -3,
    background: 'transparent',
    cursor: 'row-resize',
    textAlign: 'center'
}

